import useCurrentAccount from 'hooks/useCurrentAccount';
import React, { useEffect, useState, useMemo } from 'react';
import Submission from 'types/Submission';
import { useTranslation } from 'react-i18next';
import Form from './Form';
import PhotoShelterGallerySelect from './PhotoShelterGallerySelect';
import {
  LumenButton,
  LumenCombobox,
  LumenModal,
  LumenModalActions,
} from '@photoshelter/socialie-components';
import { useBulkRouteSubmissionsToDestinationMutation } from 'api/submissions';
import useFlashMessages from 'hooks/useFlashMessages';

interface Props {
  submissions: Array<Submission>;
  renderTrigger: React.ReactNode;
}

interface FormState {
  destinationId: number | null;
  gallery: { id: string; label: string };
  errorMessage: string | null;
}

export default function SendSubmissionsToGallery(props: Props) {
  const [routeSubmissions, { isLoading }] =
    useBulkRouteSubmissionsToDestinationMutation();
  const { submissions, renderTrigger } = props;

  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const currentAccount = useCurrentAccount();

  // Filter valid PhotoShelter destinations
  const destinations = useMemo(() => {
    return (
      currentAccount?.destinations.filter((d) => {
        if (submissions.length > 1) {
          return d.destinationType === 'photo_shelter';
        }

        const routingEvents = submissions[0].routingEvents;
        return (
          d.destinationType === 'photo_shelter' &&
          !routingEvents?.some(
            (e) => e.destinationExternalId === d.config.orgId
          )
        );
      }) || []
    );
  }, [currentAccount, submissions]);

  const destinationIds = useMemo(() => {
    return destinations.map((d) => d.id);
  }, [destinations]);

  const [formState, setFormState] = useState<FormState>({
    destinationId: destinationIds[0],
    gallery: { id: '', label: '' },
    errorMessage: null,
  });

  // If the destinations change, reset the form state. Note that we
  // need to use JSON.stringify to compare the destinationIds array
  // because useMemo does not do a deep comparison.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setFormState({
      destinationId: destinationIds[0],
      gallery: { id: '', label: '' },
      errorMessage: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(destinationIds)]);

  const { gallery, errorMessage } = formState;
  const destination = formState.destinationId
    ? destinations.find((d) => d.id === formState.destinationId)
    : null;

  const { addFlashMessage } = useFlashMessages();

  const updateFormState = (updates: Partial<FormState>) => {
    setFormState((prevState) => ({ ...prevState, ...updates }));
  };

  const resetFormState = () => {
    setFormState({
      destinationId: destinationIds[0],
      gallery: { id: '', label: '' },
      errorMessage: null,
    });
  };

  const handleSubmit = async () => {
    if (!destination) {
      updateFormState({
        errorMessage: t('sendSubmissionsToGallery.destinationRequired'),
      });
      return;
    }

    if (!gallery.id) {
      updateFormState({
        errorMessage: t('sendSubmissionsToGallery.galleryRequired'),
      });
      return;
    }

    const result = await routeSubmissions({
      ids: submissions.map((s) => s.id),
      destinationId: destination.id,
      path: gallery.id,
      displayPath: gallery.label,
    });

    if ('error' in result) {
      console.error(result.error);
      updateFormState({ errorMessage: t('sendSubmissionsToGallery.error') });
      return;
    }

    addFlashMessage(
      t('sendSubmissionsToGallery.destinationSuccess', {
        count: submissions.length,
      })
    );

    resetFormState();

    setIsOpen(false);
  };

  if (!currentAccount || !destination) return null;

  return (
    <>
      <LumenModal
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        size="md"
        title={t('sendSubmissionsToGallery.heading')}
        renderTrigger={renderTrigger}
      >
        <Form onSubmit={handleSubmit} className="mb-2 last:mb-0">
          {destinations.length > 1 ? (
            <Form.Field label={t('sendSubmissionsToGallery.selectDestination')}>
              <LumenCombobox
                options={destinations.map((d) => ({
                  label: d.config.label,
                  value: d.id.toString(),
                }))}
                popoverProps={{ portal: false }}
                width="100%"
                value={{
                  value: destination.id.toString(),
                  label: destination.config.label,
                }}
                onValueChange={(selectedOption) => {
                  const selectedDestination = destinations.find(
                    (d) => d.id.toString() === selectedOption?.value
                  );
                  updateFormState({
                    destinationId: selectedDestination?.id,
                    gallery: { id: '', label: '' },
                    errorMessage: null,
                  });
                }}
              />
            </Form.Field>
          ) : (
            <Form.TextInput
              label={t('sendSubmissionsToGallery.destination')}
              value={destination.config.label}
              disabled
            />
          )}

          <PhotoShelterGallerySelect
            key={destination?.id ?? 'no-org'}
            value={gallery}
            label={t('sendSubmissionsToGallery.gallery')}
            onChange={(newGallery) => updateFormState({ gallery: newGallery })}
            destinationId={destination.id}
            autoFocus={destinations.length < 2}
          />
          {errorMessage && (
            <div className="notice notice--error">{errorMessage}</div>
          )}

          <LumenModalActions>
            <LumenButton onClick={handleSubmit} isLoading={isLoading}>
              {t('sendSubmissionsToGallery.send')}
            </LumenButton>
          </LumenModalActions>
        </Form>
      </LumenModal>
    </>
  );
}
